import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject } from 'rxjs';

export interface Theme {
  id: string;
  name: string;
  colors: {
    start: string;
    middle: string;
    end: string;
  };
}

export const THEMES: Theme[] = [
  {
    id: 'violet',
    name: 'Violet Theme',
    colors: {
      start: '#8b9df3',
      middle: '#a78bfa', 
      end: '#c4b5fd'
    }
  },
  {
    id: 'blue',
    name: 'Blue Theme',
    colors: {
      start: '#667eea',
      middle: '#764ba2',
      end: '#667eea'
    }
  },
  {
    id: 'lightblue',
    name: 'Light Blue Theme',
    colors: {
      start: '#74b9ff',
      middle: '#0984e3',
      end: '#a29bfe'
    }
  }
];

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private currentThemeSubject = new BehaviorSubject<Theme>(THEMES[1]);
  public currentTheme$ = this.currentThemeSubject.asObservable();

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.loadTheme();
  }

  private loadTheme(): void {
    if (isPlatformBrowser(this.platformId)) {
      const savedThemeId = localStorage.getItem('selected-theme');
      const theme = THEMES.find(t => t.id === savedThemeId) || THEMES[1];
      this.setTheme(theme);
    }
  }

  setTheme(theme: Theme): void {
    this.currentThemeSubject.next(theme);
    this.applyTheme(theme);
    
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem('selected-theme', theme.id);
    }
  }

  private applyTheme(theme: Theme): void {
    if (isPlatformBrowser(this.platformId)) {
      const root = document.documentElement;
      root.style.setProperty('--brand-gradient-start', theme.colors.start);
      root.style.setProperty('--brand-gradient-middle', theme.colors.middle);
      root.style.setProperty('--brand-gradient-end', theme.colors.end);
      
      // Update gradient variables
      const gradient = `linear-gradient(135deg, ${theme.colors.start} 0%, ${theme.colors.middle} 50%, ${theme.colors.end} 100%)`;
      root.style.setProperty('--brand-gradient', gradient);
      
      // Update subtle gradient
      const subtleGradient = `linear-gradient(135deg, ${this.hexToRgba(theme.colors.start, 0.1)} 0%, ${this.hexToRgba(theme.colors.middle, 0.1)} 50%, ${this.hexToRgba(theme.colors.end, 0.1)} 100%)`;
      root.style.setProperty('--brand-gradient-subtle', subtleGradient);
    }
  }

  private hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  getCurrentTheme(): Theme {
    return this.currentThemeSubject.value;
  }

  getAvailableThemes(): Theme[] {
    return THEMES;
  }
}